import React from 'react';
import { Download, ExternalLink } from 'lucide-react';
import type { InvoiceRow } from '../../lib/billingApi';
import { track } from '../../utils/analytics';

function formatMoney(cents: number, currency: string) {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatDateFromUnix(ts: number) {
  return new Date(ts * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ status }: { status: string | null }) {
  const s = (status ?? 'unknown').toLowerCase();
  const cls =
    s === 'paid'
      ? 'bg-green-100 text-green-800'
      : s === 'open' || s === 'draft'
        ? 'bg-yellow-100 text-yellow-800'
        : s === 'void' || s === 'uncollectible'
          ? 'bg-red-100 text-red-800'
          : 'bg-gray-100 text-gray-700';
  return <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{s}</span>;
}

export function BillingHistoryTable(props: {
  invoices: InvoiceRow[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}) {
  const { invoices, loading, hasMore, onLoadMore } = props;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200">
        <h4 className="font-semibold text-black">Billing History</h4>
        <p className="text-xs text-gray-600 mt-1">Invoices are fetched from Stripe in real time.</p>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-gray-600">Loading invoices…</div>
      ) : invoices.length === 0 ? (
        <div className="p-4 text-sm text-gray-600">No invoices yet.</div>
      ) : (
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td className="px-4 py-3 text-sm text-gray-900">{formatDateFromUnix(inv.created)}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{formatMoney(inv.amount_paid ?? 0, inv.currency ?? 'usd')}</td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <StatusBadge status={inv.status} />
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-3">
                    {inv.invoice_pdf && (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-black"
                        onClick={() => track('Invoice Downloaded', { invoice_id: inv.id })}
                      >
                        <Download className="w-4 h-4" />
                        <span>PDF</span>
                      </a>
                    )}
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-black"
                        onClick={() => track('Invoice Viewed', { invoice_id: inv.id })}
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View</span>
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {hasMore && !loading && (
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onLoadMore}
            className="w-full px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}


